import React, { useState, useEffect } from 'react';
import { Typography, Radio, RadioGroup, FormControlLabel, FormControl, Checkbox } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../auth/Auth';

const App = () => {
  const { user } = useAuth();
  const [isUploaded, setIsUploaded] = useState(false);
  const [language, setLanguage] = useState('uk-UA');
  const [bank, setBank] = useState('privatbank');
  const [downloadPath, setDownloadPath] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [convertStatus, setConvertStatus] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [splitAcquiring, setSplitAcquiring] = useState(false);
  const [splitAcquiringPumb, setSplitAcquiringPumb] = useState(false);
  const [splitAcquiringMtb, setSplitAcquiringMtb] = useState(false);
  const [contractor, setContractor] = useState(null);
  const [contractorId, setContractorId] = useState(null);
  const [contractorRestaurants, setContractorRestaurants] = useState([]);
  const [contractorEmails, setContractorEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversionError, setConversionError] = useState('');
  const [invalidEdrpou, setInvalidEdrpou] = useState(false);
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [showManualEdrpou, setShowManualEdrpou] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [userAccessRights, setUserAccessRights] = useState(null);
  const [fopMatchError, setFopMatchError] = useState('');
  const [translations, setTranslations] = useState(null);
  const [rightsConfig, setRightsConfig] = useState(null);
  const [contractorRegion, setContractorRegion] = useState('uk-UA');
  const [kurs, setKurs] = useState('1'); // Нове поле для курсу

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = user?.user_id;
        if (!userId) {
          setError('Користувач не авторизований');
          setLoading(false);
          return;
        }
    
        // 1. Отримуємо права користувача
        try {
          const rightsResponse = await axios.get(`https://bank2.daolog.net/user-rights/${userId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });
          
          let accessRights = 2;
          
          const userContractorResponse = await axios.get(`https://bank2.daolog.net/user-contractor-sup-sup-nnn/${userId}`);
          const contractorIdFromResponse = userContractorResponse.data.contractor_id;
          
          if (contractorIdFromResponse) {
            setContractorId(contractorIdFromResponse);
            
            try {
              const rightsConfigResponse = await axios.get(`https://bank2.daolog.net/api/rights-config/${contractorIdFromResponse}`);
              setRightsConfig(rightsConfigResponse.data);
              
              if (rightsConfigResponse.data && rightsConfigResponse.data.status === "1") {
                accessRights = 1;
              } else if (rightsConfigResponse.data && rightsConfigResponse.data.rights) {
                accessRights = parseInt(rightsConfigResponse.data.rights);
              }
            } catch (configError) {
              console.error('Помилка завантаження конфігу прав:', configError);
              if (rightsResponse.data && rightsResponse.data.status) {
                accessRights = parseInt(rightsResponse.data.status);
              }
            }
          }
          
          setUserAccessRights(accessRights);
        } catch (error) {
          console.error('Error fetching user rights:', error);
          setUserAccessRights(2);
        }

        // 2. Отримуємо регіон контрагента
        const userContractorResponse = await axios.get(`https://bank2.daolog.net/user-contractor-sup-sup-nnn/${userId}`);
        const contractorIdFromResponse = userContractorResponse.data.contractor_id;

        if (!contractorIdFromResponse) {
          setError('Contractor ID not found');
          setLoading(false);
          return;
        }

        setContractorId(contractorIdFromResponse);
        
        // Отримуємо конфіг регіону контрагента
        try {
          const contractorRegionResponse = await axios.get(`https://bank2.daolog.net/contractor-region/${contractorIdFromResponse}`);
          const regionFromConfig = contractorRegionResponse.data.region;
          setContractorRegion(regionFromConfig || 'uk-UA');
          
          // Встановлюємо банк за замовчуванням в залежності від регіону контрагента
          let defaultBank = 'privatbank';
          if (regionFromConfig === 'pl-PL') {
            defaultBank = 'ipko';
          } else if (regionFromConfig === 'cs-CZ') {
            defaultBank = 'monetabank';
          }
          setBank(defaultBank);
          
        } catch (regionError) {
          console.error('Помилка отримання регіону контрагента:', regionError);
          setContractorRegion('uk-UA');
          setBank('privatbank');
        }

        // 3. Отримуємо регіон користувача для перекладів
        const regionResponse = await axios.get(`https://bank2.daolog.net/user-region/${userId}`);
        const userRegion = regionResponse.data.region || 'uk-UA';
        setLanguage(userRegion);
        
        // Отримуємо переклади
        const translationsResponse = await axios.get(`https://bank2.daolog.net/translations/${userRegion}`);
        setTranslations(translationsResponse.data);

        // 4. Отримуємо дані контрактора та ресторанів
        const [contractorResponse, restaurantsResponse] = await Promise.all([
          axios.get(`https://bank2.daolog.net/contractor-details-nnn/${userId}`),
          axios.get(`https://bank2.daolog.net/user-contractor-restaurants-adm-nnn/${userId}`)
        ]);

        const contractorData = contractorResponse.data;
        setContractor(contractorData);
        setContractorRestaurants(restaurantsResponse.data.restaurants);
        
        // 5. Зберігаємо контрактора в localStorage
        localStorage.setItem('contractor', JSON.stringify(contractorData));
        
        // Генеруємо конфіг прав, якщо його немає
        try {
          await axios.post('https://bank2.daolog.net/api/generate-rights-config', { contractorId: contractorIdFromResponse });
          
          const rightsConfigResponse = await axios.get(`https://bank2.daolog.net/api/rights-config/${contractorIdFromResponse}`);
          setRightsConfig(rightsConfigResponse.data);
        } catch (error) {
          console.error('Помилка при завантаженні конфігу прав:', error);
          
          try {
            await axios.post('https://bank2.daolog.net/api/generate-rights-config', { contractorId: contractorIdFromResponse });
            const newRightsConfigResponse = await axios.get(`https://bank2.daolog.net/api/rights-config/${contractorIdFromResponse}`);
            setRightsConfig(newRightsConfigResponse.data);
          } catch (retryError) {
            console.error('Не вдалося створити конфіг прав:', retryError);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Помилка при отриманні даних:', error);
        setError('Помилка при отриманні даних');
        setLoading(false);
      }
    };
  
    if (user) {
      fetchData();
    }
  }, [user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    if (!showManualEdrpou) {
      // Для всіх регіонів використовуємо значення "1" за замовчуванням
      formData.set('rmsid', '1');
    }

    formData.append('splitAcquiring', splitAcquiring);
    formData.append('splitAcquiringPumb', splitAcquiringPumb);
    formData.append('splitAcquiringMtb', splitAcquiringMtb);
    formData.append('bank', bank);
    formData.append('username', user.username);
    formData.append('userId', user.user_id);
    formData.append('contractorId', contractorId);
    formData.append('submitDate', new Date().toISOString());
    
    // Додаємо курс тільки для monetabank
    if (bank === 'monetabank' || bank === 'ray-ch') {
      formData.append('kurs', kurs);
    }
    
    // Використовуємо права з конфігу для визначення типу
    const versionType = userAccessRights === 2 ? 'demo' : 'regular';
    formData.append('type', versionType);
    formData.append('originalPath', selectedFile?.name || ''); 

    const fopsData = [];
    contractorRestaurants.forEach(restaurant => {
      restaurant.fops
        .filter(fop => !fop.name_fop.includes('_deactivated'))
        .forEach(fop => {
          fopsData.push({
            name: fop.name_fop,
            edrpou: fop.edrpou
          });
        });
    });
    formData.append('fopsData', JSON.stringify(fopsData));

    try {
      const response = await fetch('https://bank2.daolog.net/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setDownloadPath(data.filePath);
        setIsUploaded(true);
        setConvertStatus('success');
        setIsSubmitted(true);
        setConversionError('');
        setInvalidEdrpou(false);
        setFopMatchError('');
      } else {
        if (data.error === 'conversion_error') {
          setConvertStatus('error');
          setConversionError(translations?.conversionError || 'Помилка конвертації файлу');
          setFopMatchError('');
          setInvalidEdrpou(false);
          setIsSubmitted(true);
        } else if (data.error === 'fop_not_found') {
          setFopMatchError(language === 'pl-PL' ? 
            'Bieżący FOP nie jest włączony do Twojego konta! Złóż wniosek o dodanie tego FOPa do swojego pakietu lub skontaktuj się z nami - nasi specjaliści pomogą w rozwiązaniu tego problemu.' : 
            language === 'cs-CZ' ?
            'Současný OSVČ není součástí vašeho účtu! Pošlete žádost o přidání tohoto OSVČ do vašeho balíčku nebo nás kontaktujte - naši specialisté vám pomohou vyřešit tento problém.' :
            'Поточний ФОП не включений у Ваш кабінет! Надайте заявку на додавання цього ФОПа у Ваш пакет або ж зв\'яжіться з нами - і наші спеціалісти допоможуть у вирішенні цього питання.');
          setConvertStatus('error');
          setConversionError('');
          setInvalidEdrpou(false);
          setIsSubmitted(true);
        } else {
          setConvertStatus('error');
          setConversionError(translations?.conversionError || 'Помилка конвертації файлу');
          setFopMatchError('');
          setInvalidEdrpou(false);
          setIsSubmitted(true);
        }
      }
    } catch (error) {
      setConvertStatus('error');
      setConversionError(translations?.conversionError || 'Помилка конвертації файлу');
      setFopMatchError('');
      setInvalidEdrpou(false);
      setIsSubmitted(true);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const fileContent = e.target.result;
        const base64Content = arrayBufferToBase64(fileContent);
        
        try {
          const response = await axios.post('https://bank2.daolog.net/save-original-file', 
            {
              userId: user.user_id,
              bankType: bank,
              originalPath: file.name,
              fileContent: base64Content
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              }
            }
          );
          
          if (response.data.message) {
            console.log('Файл успішно збережено:', response.data.message);
          }
        } catch (error) {
          console.error('Помилка збереження оригінального файлу:', error.response?.data?.error || error.message);
          setConversionError('Помилка збереження оригінального файлу. Спробуйте ще раз.');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const handleSelectFileClick = () => {
    document.querySelector('input[type="file"]').click();
  };

  const handleBankChange = (event) => {
    setBank(event.target.value);
    setSplitAcquiring(false);
    setSplitAcquiringMtb(false);
    setSplitAcquiringPumb(false);
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (bank === 'san') {
        const response = await fetch(
          `https://bank2.daolog.net/download-san/${user.user_id}/${encodeURIComponent(downloadPath)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Помилка завантаження файлу');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `san_${new Date().getTime()}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setConvertStatus('success');
        setIsUploaded(true);
        return;
      }
      
      if (bank === 'santander') {
        const response = await fetch(
          `https://bank2.daolog.net/download-nnn/${user.user_id}/${encodeURIComponent(
            downloadPath.replace(/^.*[\\\/]/, '')
          )}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Помилка завантаження файлу');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `santander_${new Date().getTime()}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setConvertStatus('success');
        setIsUploaded(true);
        return;
      }
      
      // Стандартна обробка для інших банків
      const response = await fetch(
        `https://bank2.daolog.net/download-nnn/${user.user_id}/${encodeURIComponent(downloadPath)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Помилка завантаження файлу');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      let fileName = '';
if (bank === 'millennium') {
  fileName = `millennium_${new Date().getTime()}.txt`;
} else if (bank === 'ing') {
  fileName = `ing_${new Date().getTime()}.txt`;
} else if (bank === 'monetabank') {
  fileName = `monetabank_${new Date().getTime()}.txt`;
} else if (bank === 'ray-ch') {
  fileName = `ray-ch_${new Date().getTime()}.txt`;
} else {
  fileName = `bank_${new Date().getTime()}.txt`;
}
      
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setConvertStatus('success');
      setIsUploaded(true);
      
    } catch (error) {
      console.error('Download error:', error);
      setConversionError(translations?.conversionError || 'Помилка конвертації файлу');
      setConvertStatus('error');
    }
  };

  const handleContainerClick = () => {
    setShowForm(true);
  };

  const toggleClientInfo = () => {
    setShowClientInfo(!showClientInfo);
  };

  const handleReturnClick = () => {
    setIsSubmitted(false);
    setConvertStatus('');
    setConversionError('');
    setFopMatchError('');
    setInvalidEdrpou(false);
  };

  const renderBankOptions = () => {
    // Використовуємо contractorRegion для визначення переліку банків
    if (contractorRegion === 'pl-PL') {
      return (
        <>
          <FormControlLabel value="ipko" control={<Radio />} label="IPKO" />
          <FormControlLabel value="paribas" control={<Radio />} label="Paribas" />
          <FormControlLabel value="mbank" control={<Radio />} label="Mbank" />
          <FormControlLabel value="millenium" control={<Radio />} label="Millenium" />
          <FormControlLabel value="pko" control={<Radio />} label="PKO" />
          <FormControlLabel value="san" control={<Radio />} label="Santader" />
        </>
      );
    } else if (contractorRegion === 'cs-CZ') {
      // Чеські банки - тільки MONETA Bank
      return (
        <>
          <FormControlLabel value="monetabank" control={<Radio />} label="MONETA Bank" />
          <FormControlLabel value="ray-ch" control={<Radio />} label="Raiffeisen bank" />
        </>
        
      );
    } else {
      // Для всіх інших регіонів (uk-UA за замовчуванням) показуємо українські банки
      return (
        <>
          <FormControlLabel
            value="privatbank"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>ПриватБанк</span>
                {bank === 'privatbank' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="pumb"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>ПУМБ</span>
                {bank === 'pumb' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiringPumb}
                        onChange={(e) => setSplitAcquiringPumb(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="mtb"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>МТБ</span>
                {bank === 'mtb' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiringMtb}
                        onChange={(e) => setSplitAcquiringMtb(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel value="yniversal" control={<Radio />} label="Універсал Банк / Таском банк" />
          <FormControlLabel
            value="mono"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>Монобанк</span>
                {bank === 'mono' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="ray"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>Райфайзен</span>
                {bank === 'ray' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="ykr"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>УкрсібБанк</span>
                {bank === 'ykr' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="piv"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>Південний</span>
                {bank === 'piv' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="oshchad"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>Ощадбанк</span>
                {bank === 'oshchad' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="abank"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>А-банк</span>
                {bank === 'abank' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="otp"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>ОТП-банк</span>
                {bank === 'otp' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="vos"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>Банк Восток</span>
                {bank === 'vos' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
          <FormControlLabel
            value="kredo"
            control={<Radio />}
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>KredoBank</span>
                {bank === 'kredo' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitAcquiring}
                        onChange={(e) => setSplitAcquiring(e.target.checked)}
                      />
                    }
                    label={translations?.splitAcquiring || 'Розділяти еквайрінг на дві проводки'}
                    style={{ marginLeft: '10px' }}
                  />
                )}
              </div>
            }
          />
        </>
      );
    }
  };

  const getManualEdrpouText = () => {
    switch (language) {
      case 'pl-PL':
        return 'Wprowadź EDRPOU ręcznie';
      case 'cs-CZ':
        return 'Zadat IČO ručně';
      default:
        return 'Вказати ЄДРПОУ вручну';
    }
  };

  const getRmsidPlaceholder = () => {
    switch (language) {
      case 'pl-PL':
        return 'RMSINN';
      case 'cs-CZ':
        return 'Kurs';
      default:
        return 'РМСІНН';
    }
  };

  if (loading || !translations) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="br-m" style={{ marginTop: "120px" }}>
      <div className="container-about">
        <div style={{ marginBottom: '20px' }}>
          <Typography variant="h6" gutterBottom>
            <b>{translations.client}:</b> {contractor?.name}
          </Typography>
        </div>

        {!showForm ? (
          <div
            className="clickable-container"
            onClick={handleContainerClick}
            style={{
              cursor: 'pointer',
              padding: '20px',
              border: '1px solid #ccc',
              borderRadius: '5px'
            }}
          >
            <Typography variant="h5" gutterBottom>
              {translations.downloadStatement}
            </Typography>
          </div>
        ) : isSubmitted && convertStatus === 'success' ? (
          <div className="form-container">
            <Typography variant="h6" gutterBottom>
              {translations.bankStatement}
            </Typography>
            <div>
              <Typography>
                {translations.statementSuccess}
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button
                variant="contained"
                className="download-button-ch"
                onClick={handleDownload}
                style={{ flex: 1 }}
              >
                {translations.getStatement}
              </button>
              <button
                variant="contained"
                className="download-button-ch"
                onClick={handleReturnClick}
                style={{ flex: 1 }}
              >
                {translations.returnToPolish}
              </button>
            </div>
          </div>
        ) : isSubmitted && convertStatus === 'error' ? (
          <div className="form-container">
            <Typography variant="h6" gutterBottom>
              {translations.error}
            </Typography>
            <div>
              <Typography style={{ color: "red" }}>
                {fopMatchError || conversionError || translations.conversionError}
              </Typography>
            </div>
            <button
              style={{ marginTop: "15px" }}
              variant="contained"
              className="download-button-ch"
              onClick={handleReturnClick}
              fullWidth
            >
              {translations.returnToPolish}
            </button>
          </div>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              {translations.downloadStatement}
            </Typography>
            <div className="container-about-a">
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="bank"
                  name="bank"
                  value={bank}
                  onChange={handleBankChange}
                >
                  <div
                    onClick={toggleClientInfo}
                    style={{
                      cursor: 'pointer',
                      marginBottom: '20px'
                    }}
                  >
                    <Typography variant="h5" gutterBottom>
                      {translations.clientInfo} {showClientInfo ? '▼' : '▶'}
                    </Typography>
                  </div>

                  {showClientInfo && (
                    <>
                      <div style={{ marginBottom: '20px' }}>
                        <Typography variant="h6">
                          {translations.versionType}: {userAccessRights === 2 ? translations.demoVersion : translations.regularVersion}
                        </Typography>
                      </div>

                      {contractorRestaurants.map((restaurant) => (
                        <div key={restaurant.id} style={{ marginBottom: '15px' }}>
                          <div className="fops-list">
                            {restaurant.fops
                              .filter(fop => !fop.name_fop.includes('_deactivated'))
                              .map((fop) => (
                                <div key={fop.id} className="fop-item">
                                  <span>{fop.name_fop} (ЄДРПОУ: {fop.edrpou})</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  <Typography variant="h6" gutterBottom>
                    {translations.selectBank}
                  </Typography>

                  {renderBankOptions()}
                </RadioGroup>
              </FormControl>

              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <input
                  className="input-stl"
                  style={{ display: 'none' }}
                  type="file"
                  name="myFile"
                  accept=".xls,.xlsx,.csv,.pdf"
                  onChange={handleFileSelect}
                  required
                />
                <button
                  className="download-button"
                  type="button"
                  onClick={handleSelectFileClick}
                >
                  {translations.selectFile}
                </button>
                {selectedFile && (
                  <div style={{ marginTop: "5px" }}>
                    {translations.fileSelected}: {selectedFile.name}
                  </div>
                )}

                {/* Поле курсу для monetabank */}
                {(bank === 'monetabank' || bank === 'ray-ch') && (
  <div>
    <input
      className="input-stl"
      style={{ marginTop: "10px", backgroundColor: "rgb(213, 220, 255)" }}
      type="number"
      step="0.01"
      min="0"
      name="kurs"
      placeholder="Kurs"
      value={kurs}
      onChange={(e) => setKurs(e.target.value)}
      required
    />
    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
      {language === 'cs-CZ' 
        ? 'Výchozí hodnota: 1. Zadejte kurz pro převod měny (např. 0.62)'
        : language === 'pl-PL'
        ? 'Domyślnie: 1. Wprowadź kurs wymiany waluty (np. 0.62)'
        : 'За замовчуванням: 1. Введіть курс для конвертації валюти (напр. 0.62)'}
    </div>
  </div>
)}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showManualEdrpou}
                      onChange={(e) => setShowManualEdrpou(e.target.checked)}
                    />
                  }
                  label={getManualEdrpouText()}
                  style={{ display: 'block', marginTop: '10px' }}
                />

                {showManualEdrpou && (
                  <input
                    className="input-stl"
                    style={{ marginTop: "10px", backgroundColor: "rgb(213, 220, 255)" }}
                    type="text"
                    name="rmsid"
                    placeholder={getRmsidPlaceholder()}
                    pattern="[0-9]*"
                    defaultValue="1"
                    required
                  />
                )}

                <button
                  variant="contained"
                  className="download-button-ch"
                  style={{ marginTop: "20px", marginBottom: "10px" }}
                  type="submit"
                >
                  {translations.uploadStatement}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default App;